import { useRefdataStore } from '@/store/refdataStore';
import { Address, AddressV2 } from '@/types/general';
import { isValidIBAN, electronicFormatIBAN, friendlyFormatIBAN } from 'ibantools';

const ibanFirstFour = /^[A-Z]{2}[0-9]{2}/;
const yapealIbanSpec = /^CH[0-9]{2}(83019|30334)([0-9A-Za-z]{12})$/;
const isNumericRegex = /^[\d]*$/;
const isrReferenceFormat = /^(.{2})(.{1,5})?(.{1,5})?(.{1,5})?(.{1,5})?(.{1,5})?(.+)?$/;

export function isStartOfYapealIban(iban: string): boolean {
  const elIban = electronicFormatIBAN(iban);
  return !!elIban && /^CH[0-9]{2}(83019|30334)/.test(elIban);
  // return /^CH[0-9]{2}(83019|30334)/.test(iban.replaceAll(' ', ''));
}

export function isStartOfIban(iban: string): boolean {
  const elIban = electronicFormatIBAN(iban);
  return !!elIban && ibanFirstFour.test(elIban);
}

export function isYapealIban(iban: string): boolean {
  const efIban = electronicFormatIBAN(iban) ?? '';
  return isValidIBAN(efIban) && yapealIbanSpec.test(efIban ?? '');
}

export function formatYapealIban(iban: string): string {
  const efIban = electronicFormatIBAN(iban) ?? '';
  return `${efIban.substring(0, 4)} ${efIban.substring(4, 9)} ${efIban.substring(9, 21)}`;
}

export function formatIban(number: string | undefined): string {
  if (!number) return '';
  const efIban = electronicFormatIBAN(number) ?? '';
  if (!isValidIBAN(efIban)) {
    // invalid IBAN but still try to do our best to format it correclty
    return friendlyFormatIBAN(efIban) || '';
  }
  if (isYapealIban(efIban)) {
    return formatYapealIban(efIban);
  } else {
    return friendlyFormatIBAN(efIban) || '';
  }
}

function removeLeadingZeros(number: string): string {
  return Number.parseInt(number).toString();
}

export function formatESRNumber(number: string | undefined): string {
  if (!number) return '';
  if (number.length === 9 && Number.parseInt(number) !== undefined && !Number.isNaN(Number.parseInt(number))) {
    return `${number.substring(0, 2)}-${removeLeadingZeros(number.substring(2, 8))}-${number.substring(8)}`;
  }
  return number ?? '';
}

export function formatPaymentReference(value: string | undefined, padLeft = false): string {
  if (!value || value === '') return '';

  let ref = electronicFormatIBAN(value) || '';
  if (!isNumericRegex.test(ref)) {
    // format groups of four
    return friendlyFormatIBAN(ref) || ref;
  }
  if (padLeft) {
    ref = ref.padStart(24, '0');
  }
  if (isrReferenceFormat.test(ref)) {
    const matches = ref.match(isrReferenceFormat);
    return matches ? matches.slice(1).join(' ').trim() : ref;
  }

  return ref;
}

export function formatCardNumber(value: string | undefined): string {
  if (!value) {
    return '';
  }
  let cardNb = value;
  if (value.length === 4) {
    cardNb = cardNb.padStart(16, '*');
  }
  // add space every 4 character
  return friendlyFormatIBAN(cardNb) ?? cardNb;
}

export function convertAddressToAddressV2(address: Partial<Address> | Partial<AddressV2>): Partial<AddressV2> {
  if (!address) {
    return {};
  }
  const addressV2: Partial<AddressV2> = {
    city: ((address as Partial<Address>).town ?? (address as Partial<AddressV2>).city)?.trim(),
    country: ((address as Partial<Address>).country ?? (address as Partial<AddressV2>).country)?.trim(),
    number: ((address as Partial<Address>).houseNo ?? (address as Partial<AddressV2>).number)?.trim(),
    postBoxAddrs: ((address as Partial<Address>).poBox ?? (address as Partial<AddressV2>).postBoxAddrs)?.trim(),
    postalCode: ((address as Partial<Address>).zip ?? (address as Partial<AddressV2>).postalCode)?.trim(),
    province: (address as Partial<AddressV2>).province?.trim(),
    state: (address as Partial<AddressV2>).state?.trim(),
    street: ((address as Partial<Address>).street ?? (address as Partial<AddressV2>).street)?.trim(),
    countryName: ((address as Partial<Address>).countryName ?? (address as Partial<AddressV2>).countryName)?.trim()
  };
  // Remove undefined properties
  return Object.fromEntries(Object.entries(addressV2).filter(([, value]) => value !== undefined));
}

/**
 * Formats an array of address lines into a single line address.
 * @param addressLines - An array of strings representing the address lines.
 * @param country optional - The country name to include in the formatted address.
 * @returns A single line formatted address string.
 */
export function formatAddressLinesToSingleLine(addressLines: string[] | undefined | null, country?: string): string {
  if (!addressLines || addressLines.length === 0) {
    return '';
  }
  const formattedLines = addressLines.map((line) => line.trim()).filter((line) => line !== '');
  if (formattedLines.length === 0) {
    return '';
  } else {
    return formattedLines.join(', ') + (country ? `, ${country}` : '');
  }
}

/**
 * Formats an Address or AddressV2 into a single line address.
 * @param address - An Address, AddressV2, or array of strings representing the address.
 * @returns A formatted single line address string.
 */
export async function formatAddressSingleLine(
  address: Partial<Address> | Partial<AddressV2> | null | undefined
): Promise<string> {
  if (!address) {
    return '';
  }

  const addressV2 = convertAddressToAddressV2(address);

  if (!addressV2) {
    return '';
  }
  const refdataStore = useRefdataStore();
  if (!addressV2.countryName && addressV2.country) {
    addressV2.countryName = await refdataStore.countryCodeToName(addressV2.country);
  }

  const adr: string[] = [];

  adr.push([addressV2.street?.trim(), addressV2.number?.trim()].join(' ').trim());

  if (addressV2.postalCode && addressV2.city) {
    adr.push(`${addressV2.postalCode} ${addressV2.city}`.trim());
  } else if (addressV2.postalCode) {
    adr.push(addressV2.postalCode.trim());
  } else if (addressV2.city) {
    adr.push(addressV2.city.trim());
  }

  if (addressV2.countryName) {
    adr.push(addressV2.countryName.trim());
  } else if (addressV2.country) {
    // Get country name from country code
    const countryName = await refdataStore.countryCodeToName(addressV2.country);
    adr.push(countryName || addressV2.country.trim());
  }
  return adr
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .join(', ')
    .trim();
}
